import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ONLY_CHAR_VALIDATOR_REGEX } from "@shared/constants/validator.constants";
import { Civility } from "@shared/enums/civility-type.enum";
import { ClientFields } from "@shared/enums/client-fields.enum";
import { PolicyFields } from "@shared/enums/policy-fields.enum";
import {
  getCalendarClass,
  getDropdownClass,
  getInputClass,
  getInputMaskClass,
  getIsEditable,
  getIsVisible,
} from "@shared/helpers/contract-edition.helper";
import { Country } from "@shared/models/country";
import { Member } from "@shared/models/member";
import { PhonePrefixes } from "@shared/models/phone-prefixes";
import { PolicyFieldsVM } from "@shared/models/policy-field-vm.model";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { DropdownModule } from "primeng/dropdown";
import { InputMaskModule } from "primeng/inputmask";

@Component({
  selector: "app-personal-data",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    ButtonModule,
    TranslateModule,
    CalendarModule,
    InputMaskModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./personal-data.component.html",
})
export class PersonalDataComponent implements OnInit {
  @Input() policyFieldsVM!: PolicyFieldsVM;
  @Input() phonePrefixes!: PhonePrefixes[];
  @Input() member!: Member;
  @Input() memberCopy!: Member;
  @Input() selectedPhonePrefix!: PhonePrefixes | undefined;
  @Input() countriesOptions: Country[] = [];
  @Input() hasEndorsementWait4Sign!: boolean;
  @Output() emitFormValidity = new EventEmitter<number>();
  @Output() emitPhoneMask = new EventEmitter<string>();
  @Output() emitCountryHasChange = new EventEmitter<number>();
  @Output() emitPhoneNumberHasChange = new EventEmitter<string>();
  @Output() emitPhonePrefixHasChange = new EventEmitter<PhonePrefixes>();
  @Output() emitNationalityHasChange = new EventEmitter<number>();

  PolicyFields = PolicyFields;
  ClientFields = ClientFields;
  getIsVisible = getIsVisible;
  getIsEditable = getIsEditable;
  getInputClass = getInputClass;
  getDropdownClass = getDropdownClass;
  getInputMaskClass = getInputMaskClass;
  getCalendarClass = getCalendarClass;

  readonly defaultPhonePattern = "6 10 20 30 40";
  readonly defaultPhoneMask = "9 99 99 99 99";
  readonly minimalAge = 18;
  phonePattern = this.defaultPhonePattern;
  phoneMask = this.defaultPhoneMask;
  civilityOptions = Object.keys(Civility)
    .filter(key => isNaN(Number(key)))
    .map(key => ({
      label: key,
      value: Civility[key as keyof typeof Civility],
    }));
  yesNoValues: { label: string; value: boolean }[] = [
    { label: "Oui", value: true },
    { label: "Non", value: false },
  ];

  civilityHasChanged = false;
  firstNameHasChanged = false;
  memberBirthDateHasChanged = false;
  birthCityHasChanged = false;
  birthZipCodeHasChanged = false;
  birthCountryHasChanged = false;
  emailHasChanged = false;
  lastNameHasChanged = false;
  phonePrefixHasChanged = false;
  phoneNumberHasChanged = false;
  address1HasChanged = false;
  address2HasChanged = false;
  zipCodeHasChanged = false;
  cityHasChanged = false;
  emailConsentHasChanged = false;
  smsConsentHasChanged = false;
  isFirstnameValid = true;
  nationalityHasChanged = false;
  isLastnameValid = true;
  isPhoneNumberValid = true;

  minTerminationDate: Date | null | undefined;
  maxTerminationDate: Date | null | undefined;

  ngOnInit(): void {
    this.getCountry();
    const today = new Date();
    this.maxTerminationDate = new Date(today.getFullYear() - this.minimalAge, today.getMonth(), today.getDate());
    this.refreshHasChanged();
  }

  getCountry() {
    const countryId = this.countriesOptions.find(x => x.countryNameFr === this.memberCopy.birthCountry)?.id;
    this.emitCountryHasChange.emit(countryId);
  }

  onCivilityHasChanged() {
    this.civilityHasChanged = this.memberCopy.civilityId !== this.member.civilityId;
  }

  onFirstNameHasChanged() {
    this.firstNameHasChanged = this.memberCopy.firstName !== this.member.firstName;
    this.isFirstnameValid = ONLY_CHAR_VALIDATOR_REGEX.test(this.memberCopy.firstName);
    this.emitFormValidity.emit(1);
  }

  onLastNameHasChanged() {
    this.lastNameHasChanged = this.memberCopy.lastName !== this.member.lastName;
    this.isLastnameValid = ONLY_CHAR_VALIDATOR_REGEX.test(this.memberCopy.lastName);
    this.emitFormValidity.emit(1);
  }

  onMemberBirthDateChange(event: any) {
    if (event === null) {
      this.memberBirthDateHasChanged = true;
    } else {
      this.memberBirthDateHasChanged = this.member.birthDate !== event;
    }
    this.emitFormValidity.emit(1);
  }

  onBirthCityHasChanged() {
    this.birthCityHasChanged = this.memberCopy.birthCity !== this.member.birthCity;
    this.emitFormValidity.emit(1);
  }

  onBirthCountryHasChanged() {
    this.birthCountryHasChanged = this.memberCopy.birthCountry !== this.member.birthCountry;
    const countryId = this.countriesOptions.find(x => x.countryNameFr === this.memberCopy.birthCountry)?.id;
    this.emitCountryHasChange.emit(countryId);
    this.emitFormValidity.emit(1);
  }

  onNationalityHasChange() {
    this.nationalityHasChanged = this.memberCopy.nationality !== this.member.nationality;
    const id = this.countriesOptions.find(x => x.countryNameFr === this.memberCopy.nationality)?.id;
    this.emitNationalityHasChange.emit(id);
  }

  onEmailHasChanged() {
    this.emailHasChanged = this.memberCopy.email !== this.member.email;
    this.emitFormValidity.emit(1);
  }

  onPhonePrefixChange(event: PhonePrefixes) {
    if (!event) {
      return;
    }
    const additionalDigitsRequired = event.additionalDigitsRequired;
    this.updatePhoneFormat(additionalDigitsRequired);
    this.phonePrefixHasChanged = !this.selectedPhonePrefix
      ? false
      : this.selectedPhonePrefix?.prefix !== this.memberCopy.phonePrefix;
    this.emitPhonePrefixHasChange.emit(this.selectedPhonePrefix);
  }

  onPhoneNumberHasChanged() {
    this.phoneNumberHasChanged = this.memberCopy.phoneNumber !== this.member.phoneNumber;
    this.isPhoneNumberValid =
      this.memberCopy.phoneNumber.length === this.phoneMask.replace(/\s/g, "").length ||
      this.memberCopy.phoneNumber === "";
    this.emitPhoneNumberHasChange.emit(this.memberCopy.phoneNumber);
    this.emitFormValidity.emit(1);
  }

  onAddress1HasChanged() {
    this.address1HasChanged = this.memberCopy.address1 !== this.member.address1;
    this.emitFormValidity.emit(1);
  }

  onAddress2HasChanged() {
    this.address2HasChanged = this.memberCopy.address2 !== this.member.address2;
    this.emitFormValidity.emit(1);
  }

  onZipCodeHasChanged() {
    this.zipCodeHasChanged = this.memberCopy.zipCode !== this.member.zipCode;
    this.emitFormValidity.emit(1);
  }

  onBirthZipCodeHasChanged() {
    this.birthZipCodeHasChanged = this.memberCopy.birthZipCode !== this.member.birthZipCode;
  }

  onCityHasChanged() {
    this.cityHasChanged = this.memberCopy.addressCity !== this.member.addressCity;
    this.emitFormValidity.emit(1);
  }

  onEmailConsentHasChanged() {
    this.emailConsentHasChanged = this.memberCopy.isEmailConsents !== this.member.isEmailConsents;
  }

  onSmsConsentHasChanged() {
    this.smsConsentHasChanged = this.memberCopy.isSmsConsents !== this.member.isSmsConsents;
  }

  updatePhoneFormat(digitsCount: number) {
    switch (digitsCount) {
      case 6:
        this.phoneMask = "99 99 99";
        this.phonePattern = "75 10 20";
        break;
      case 8:
        this.phoneMask = "99 99 99 99";
        this.phonePattern = "87 10 20 30";
        break;
      case 9:
      default:
        this.phoneMask = this.defaultPhoneMask;
        this.phonePattern = this.memberCopy.phoneNumber ?? this.defaultPhonePattern;
    }
    this.emitPhoneMask.emit(this.phoneMask);
  }

  refreshHasChanged() {
    if (!this.member) {
      return;
    }
    const parsedBirhtDate = new Date(this.member.birthDate);
    this.memberBirthDateHasChanged = this.memberCopy.birthDate.getDate() !== parsedBirhtDate.getDate();
    this.birthZipCodeHasChanged = this.memberCopy.birthZipCode !== this.member.birthZipCode;
    this.nationalityHasChanged = this.memberCopy.nationality !== this.member.nationality;
    this.civilityHasChanged = this.memberCopy.civilityId !== this.member.civilityId;
    this.firstNameHasChanged = this.memberCopy.firstName !== this.member.firstName;
    this.lastNameHasChanged = this.memberCopy.lastName !== this.member.lastName;
    this.birthCityHasChanged = this.memberCopy.birthCity !== this.member.birthCity;
    this.birthCountryHasChanged = this.memberCopy.birthCountry !== this.member.birthCountry;
    this.emailHasChanged = this.memberCopy.email !== this.member.email;
    this.phonePrefixHasChanged = !this.selectedPhonePrefix
      ? false
      : this.selectedPhonePrefix?.prefix !== this.memberCopy.phonePrefix;
    this.phoneNumberHasChanged = this.memberCopy.phoneNumber !== this.member.phoneNumber;
    this.address1HasChanged = this.memberCopy.address1 !== this.member.address1;
    this.address2HasChanged = this.memberCopy.address2 !== this.member.address2;
    this.zipCodeHasChanged = this.memberCopy.zipCode !== this.member.zipCode;
    this.cityHasChanged = this.memberCopy.addressCity !== this.member.addressCity;
    this.emailConsentHasChanged = this.memberCopy.isEmailConsents !== this.member.isEmailConsents;
    this.smsConsentHasChanged = this.memberCopy.isSmsConsents !== this.member.isSmsConsents;
  }

  isDateInvalid() {
    return this.memberCopy.birthDate === null || isNaN(this.memberCopy.birthDate.getTime());
  }
}
